import { faArrowsRotate } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Flex, Line } from 'components';
import { H2, P } from 'components/Typography/Typography';
import { Button } from 'components/_form';
import { t } from 'i18next';
import { useEffect, useMemo, useState } from 'react';
import { toast } from 'react-toastify';
import styled from 'styled-components';
import {
  patchAccessRangeForRoles,
  type AccessRangeForRoleNestedAttribute,
  type IAccessRangeForRolesPatchBody
} from 'api/accessRangeForRoles';
import { accessRangeForRolesKeys } from 'api/queryKeys';
import { useAccessRangeForRolesQuery } from 'hooks/queries/useAccessRangeForRolesQuery';
import { useMainCompanyQuery } from 'hooks/queries/useMainCompanyQuery';
import type { AccessAccountRole, AccessAreaScope } from './UsersPermissionForm.constants';
import {
  accessAreaScopesInTableOrder,
  accessScopeLabelKeys,
  crossEmoji,
  diskEmoji,
  eyesEmoji,
  functionalUserRights,
  handEmoji,
  mergeServerAccessRanges,
  nibEmoji,
  usersRoles
} from './UsersPermissionForm.constants';

const StyledTable = styled.table`
  border-spacing: 0px;
`;

const OverflowFlex = styled(Flex)`
  overflow: auto;
  width: 100%;
`;

const CenteredTd = styled.td`
  text-align: center;
  align-items: center;
  padding: 5px;
  font-weight: 700;
`;

const BottomRightBorderCenteredTd = styled(CenteredTd)`
  border-bottom: 1px solid black;
  border-right: 1px solid black;
`;

const BottomBorderCenteredTd = styled(CenteredTd)`
  border-bottom: 1px solid black;
`;

const BottomBorderTd = styled.td`
  border-bottom: 1px solid black;
  font-weight: 700;
`;

const RightBorderTd = styled.td`
  border-right: 1px solid black;
  font-weight: 700;
`;

const BottomRightBorderTd = styled.td`
  border-bottom: 1px solid black;
  border-right: 1px solid black;
  padding: 5px;
  font-weight: 700;
`;

const AccessExamplesDashList = styled.ul`
  list-style: none;
  margin: 0;
  padding: 16px;
  padding-bottom: 0;
  font-size: ${({ theme }) => theme.textStyles.body.fontSize}px;
  line-height: ${({ theme }) => theme.textStyles.body.lineHeight};
  color: ${({ theme }) => theme.palette.text.primary};
  font-weight: 400;
`;

const AccessExamplesDashItem = styled.li`
  position: relative;
  padding-left: 1em;
  margin-bottom: 0.35em;

  &::before {
    content: '-';
    position: absolute;
    left: 0;
    font-weight: 400;
  }

  &:last-child {
    margin-bottom: 0;
  }
`;

type RoleScopeRadiosProps = {
  rowKey: string;
  groupName: string;
  selected: AccessAreaScope;
  setSelected: (scope: AccessAreaScope) => void;
  disabled: boolean;
};

const RoleViewRadios = (props: RoleScopeRadiosProps) => (
  <>
    {accessAreaScopesInTableOrder.map((scope, idx) => {
      const last = idx === accessAreaScopesInTableOrder.length - 1;
      const Td = last ? BottomRightBorderCenteredTd : BottomBorderCenteredTd;
      return (
        <Td key={`${props.rowKey}-view-${scope}`}>
          <Flex justifyContent="center">
            <label style={{ cursor: props.disabled ? 'default' : 'pointer' }}>
              <input
                type="radio"
                name={`${props.groupName}-view`}
                value={scope}
                checked={props.selected === scope}
                onChange={() => props.setSelected(scope)}
                disabled={props.disabled}
                aria-label={`${props.groupName} view ${t(
                  accessScopeLabelKeys[scope]
                )}`}
              />
            </label>
          </Flex>
        </Td>
      );
    })}
  </>
);

const RoleEditRadios = (props: RoleScopeRadiosProps) => (
  <>
    {accessAreaScopesInTableOrder.map((scope, idx) => {
      const last = idx === accessAreaScopesInTableOrder.length - 1;
      const Td = last ? BottomRightBorderCenteredTd : BottomBorderCenteredTd;
      return (
        <Td key={`${props.rowKey}-edit-${scope}`}>
          <Flex justifyContent="center">
            <label style={{ cursor: props.disabled ? 'default' : 'pointer' }}>
              <input
                type="radio"
                name={`${props.groupName}-edit`}
                value={scope}
                checked={props.selected === scope}
                onChange={() => props.setSelected(scope)}
                disabled={props.disabled}
                aria-label={`${props.groupName} edit ${t(
                  accessScopeLabelKeys[scope]
                )}`}
              />
            </label>
          </Flex>
        </Td>
      );
    })}
  </>
);

export const UsersPermissionForm = () => {
  const queryClient = useQueryClient();
  const { data: mainCompany } = useMainCompanyQuery();
  const companyId = mainCompany?.id ?? '';
  const { data: rows, isPending } = useAccessRangeForRolesQuery();

  const merged = useMemo(() => {
    if (!Array.isArray(rows)) return undefined;
    return mergeServerAccessRanges(rows);
  }, [rows]);

  const [viewByRole, setViewByRole] = useState<Record<
    AccessAccountRole,
    AccessAreaScope
  > | null>(null);
  const [editByRole, setEditByRole] = useState<Record<
    AccessAccountRole,
    AccessAreaScope
  > | null>(null);
  const [idByRole, setIdByRole] = useState<
    Partial<Record<AccessAccountRole, string>> | null
  >(null);

  useEffect(() => {
    if (!merged) return;
    setViewByRole({ ...merged.view });
    setEditByRole({ ...merged.edit });
    setIdByRole({ ...merged.ids });
  }, [merged]);

  const mutationErrorToast = (error: unknown) => {
    console.error(error);
    const status =
      typeof error === 'object' && error !== null && 'status' in error
        ? (error as { status: number }).status
        : undefined;
    if (status === 403) {
      toast.error(t('common.permissionDenied'));
    } else {
      toast.error(t('common.anErrorOccurred'));
    }
  };

  const saveMutation = useMutation({
    mutationFn: ({
      cid,
      body
    }: {
      cid: string;
      body: IAccessRangeForRolesPatchBody;
    }) => patchAccessRangeForRoles(cid, body),
    onSuccess: async (_, variables) => {
      await queryClient.invalidateQueries({
        queryKey: accessRangeForRolesKeys.list(variables.cid)
      });
      toast.success(t('common.success'));
    },
    onError: mutationErrorToast
  });

  const restoreMutation = useMutation({
    mutationFn: ({
      cid,
      body
    }: {
      cid: string;
      body: IAccessRangeForRolesPatchBody;
    }) => patchAccessRangeForRoles(cid, body),
    onSuccess: async (_, variables) => {
      await queryClient.invalidateQueries({
        queryKey: accessRangeForRolesKeys.list(variables.cid)
      });
      toast.success(t('common.success'));
    },
    onError: mutationErrorToast
  });

  const incompleteFromApi = rows !== undefined && merged === null;

  const locked =
    !companyId ||
    isPending ||
    incompleteFromApi ||
    viewByRole === null ||
    editByRole === null ||
    idByRole === null ||
    saveMutation.isPending ||
    restoreMutation.isPending;

  const handleSave = () => {
    if (
      !companyId ||
      viewByRole === null ||
      editByRole === null ||
      idByRole === null
    )
      return;

    const access_range_for_roles_attributes = usersRoles.map((role) => {
      const id = idByRole[role];
      const entry = {
        account_role: role,
        view_area_scope: viewByRole[role],
        edit_area_scope: editByRole[role]
      };
      return id ? { ...entry, id } : entry;
    });

    saveMutation.mutate({
      cid: companyId,
      body: { company: { access_range_for_roles_attributes } }
    });
  };

  const handleRestoreDefaults = () => {
    if (!companyId || restoreMutation.isPending || idByRole === null) return;

    const destroyAttrs: AccessRangeForRoleNestedAttribute[] = usersRoles
      .map((role) => idByRole[role])
      .filter((id): id is string => Boolean(id))
      .map((id) => ({ id, _destroy: '1' }));

    if (destroyAttrs.length === 0) {
      void queryClient
        .refetchQueries({
          queryKey: accessRangeForRolesKeys.list(companyId)
        })
        .then(() => toast.success(t('common.success')))
        .catch(mutationErrorToast);
      return;
    }

    restoreMutation.mutate({
      cid: companyId,
      body: { company: { access_range_for_roles_attributes: destroyAttrs } }
    });
  };

  return (
    <Flex flexDirection="column">
      <H2 mt={2} variant="h2" color="coloured">
        {t('administration.rangeOfAccessToDiffrentUserAccounts')}
      </H2>
      <Line />

      <Flex p={2} flexDirection="column">
        <P variant="body" color="primary">
          <strong>
            {t(
              'administration.Bydefault,eachtypeofuserhasaccesstotheareatowhichheisassignedandtoitssub-areas'
            )}
          </strong>
        </P>

        <AccessExamplesDashList>
          <AccessExamplesDashItem>
            {t(
              'administration.addedattheGrouplevelhasaccesstoallfactoriesinthestructure'
            )}
          </AccessExamplesDashItem>
          <AccessExamplesDashItem>
            {t(
              'administration.addedatthefactorylevelhasaccesstoallareasofthefactory'
            )}
          </AccessExamplesDashItem>
          <AccessExamplesDashItem>
            {t(
              'administration.addedatthedepartmentlevelhasaccesstoitsdepartmentanditssub-areas'
            )}
          </AccessExamplesDashItem>
        </AccessExamplesDashList>

        <Flex
          mt={3}
          justifyContent="space-between"
          alignItems="center"
          flexWrap="wrap"
        >
          <P variant="body" color="primary">
            <strong>
              {t(
                'administration.Usingthecheckboxesbelowyoucanadjustthisscopeofaccess'
              )}
            </strong>
          </P>
          <Button
            variant="eucalyptus"
            type="button"
            disabled={locked}
            onClick={handleRestoreDefaults}
          >
            <FontAwesomeIcon icon={faArrowsRotate} /> {t('profile.default')}
          </Button>
        </Flex>

        {incompleteFromApi && (
          <P variant="body" color="red" mt={3}>
            {t('common.anErrorOccurred')}
          </P>
        )}

        {viewByRole && editByRole && idByRole ? (
          <>
        <OverflowFlex mt={4}>
          <StyledTable>
            <tbody>
              <tr>
                <RightBorderTd>&nbsp;</RightBorderTd>
                <BottomRightBorderCenteredTd colSpan={4}>
                  {t('administration.lawOfView')}
                </BottomRightBorderCenteredTd>
                <BottomRightBorderCenteredTd colSpan={4}>
                  {t('administration.lawOfEdit')}
                </BottomRightBorderCenteredTd>
              </tr>
              <tr>
                <BottomRightBorderCenteredTd>&nbsp;</BottomRightBorderCenteredTd>
                {accessAreaScopesInTableOrder.map((scope, idx) => {
                  const last = idx === accessAreaScopesInTableOrder.length - 1;
                  const Td = last
                    ? BottomRightBorderCenteredTd
                    : BottomBorderCenteredTd;
                  return (
                    <Td key={`h-view-${scope}`}>
                      {t(accessScopeLabelKeys[scope])}
                    </Td>
                  );
                })}
                {accessAreaScopesInTableOrder.map((scope, idx) => {
                  const last = idx === accessAreaScopesInTableOrder.length - 1;
                  const Td = last
                    ? BottomRightBorderCenteredTd
                    : BottomBorderCenteredTd;
                  return (
                    <Td key={`h-edit-${scope}`}>
                      {t(accessScopeLabelKeys[scope])}
                    </Td>
                  );
                })}
              </tr>

              {usersRoles.map((userRole) => (
                <tr key={userRole}>
                  <BottomRightBorderTd>
                    {t(`administration.${userRole}`)}
                  </BottomRightBorderTd>

                  <RoleViewRadios
                    rowKey={userRole}
                    groupName={userRole}
                    selected={viewByRole[userRole]}
                    disabled={locked}
                    setSelected={(scope) =>
                      setViewByRole((prev) =>
                        prev === null ? null : { ...prev, [userRole]: scope }
                      )
                    }
                  />
                  <RoleEditRadios
                    rowKey={userRole}
                    groupName={userRole}
                    selected={editByRole[userRole]}
                    disabled={locked}
                    setSelected={(scope) =>
                      setEditByRole((prev) =>
                        prev === null ? null : { ...prev, [userRole]: scope }
                      )
                    }
                  />
                </tr>
              ))}
            </tbody>
          </StyledTable>
        </OverflowFlex>

        <Flex mt={3} justifyContent="flex-end" width="100%">
          <Button
            variant="eucalyptus"
            type="button"
            disabled={locked}
            onClick={handleSave}
          >
            {t('common.save')}
          </Button>
        </Flex>

          </>
        ) : null}

        <Flex ml={['0', '55%']} mt={2}>
          <P variant="body" fontWeight={700}>
            {t(
              'administration.The editing right allows you to create assessments, audits, charts, plans, reports and available areas'
            )}
          </P>
        </Flex>
      </Flex>
      <H2 mt={4} variant="h2" color="coloured">
        {t('administration.FUNCTIONAL USER RIGHTS')}
      </H2>
      <Line />
      <Flex flexDirection={['column', 'row']}>
        <Flex width={['100%', '60%']}>
          <OverflowFlex mt={4}>
            <StyledTable>
              <tbody>
                <tr>
                  <BottomBorderCenteredTd />
                  <BottomBorderCenteredTd>
                    {t('administration.guest')}
                  </BottomBorderCenteredTd>
                  <BottomBorderCenteredTd>
                    {t('administration.user')}
                  </BottomBorderCenteredTd>
                  <BottomBorderCenteredTd>
                    {t('administration.admin')}
                  </BottomBorderCenteredTd>
                  <BottomBorderCenteredTd>
                    {t('administration.consultant')}
                  </BottomBorderCenteredTd>
                </tr>

                {functionalUserRights.map((functionalUserRight) => (
                  <tr key={functionalUserRight.name}>
                    <BottomBorderTd>{t(functionalUserRight.name)}</BottomBorderTd>
                    <BottomBorderCenteredTd>
                      <p>{String.fromCodePoint(functionalUserRight.guest)}</p>
                    </BottomBorderCenteredTd>
                    <BottomBorderCenteredTd>
                      {String.fromCodePoint(functionalUserRight.user)}
                    </BottomBorderCenteredTd>
                    <BottomBorderCenteredTd>
                      {String.fromCodePoint(functionalUserRight.admin)}
                    </BottomBorderCenteredTd>
                    <BottomBorderCenteredTd>
                      {String.fromCodePoint(functionalUserRight.consultant)}
                    </BottomBorderCenteredTd>
                  </tr>
                ))}
              </tbody>
            </StyledTable>
          </OverflowFlex>
        </Flex>
        <Flex
          mt={4}
          width={['100%', '40%']}
          alignItems="center"
          flexDirection="column"
        >
          <P variant="body" fontWeight={700}>
            {t('administration.legend')}:
          </P>
          <Flex flexDirection="column">
            <Flex pl={[0, 6]} pt={3}>
              {String.fromCodePoint(crossEmoji)}
              <P ml={4} variant="body" fontWeight={700}>
                {t('administration.lackOfAccess')}
              </P>
            </Flex>
            <Flex pl={[0, 6]} pt={3}>
              {String.fromCodePoint(eyesEmoji)}
              <P ml={4} variant="body" fontWeight={700}>
                {t('administration.preview')}
              </P>
            </Flex>
            <Flex pl={[0, 6]} pt={3}>
              {String.fromCodePoint(diskEmoji)}
              <P ml={4} variant="body" fontWeight={700}>
                {t('administration.downloadOption')}
              </P>
            </Flex>
            <Flex pl={[0, 6]} pt={3}>
              {String.fromCodePoint(handEmoji)}
              <P ml={4} variant="body" fontWeight={700}>
                {t('administration.selectedItems')}
              </P>
            </Flex>
            <Flex pl={[0, 6]} pt={3}>
              {String.fromCodePoint(nibEmoji)}
              <P ml={4} variant="body" fontWeight={700}>
                {t('administration.creating, editing')}
              </P>
            </Flex>
          </Flex>
        </Flex>
      </Flex>
    </Flex>
  );
};
